import { DocProgress, DocProgressMap, RegulatoryFunctionalGroup } from "@types";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetTasks,
  useGetUser,
} from "src/hooks/queries";
import {
  getDocumentContexts,
  isDocumentCompleted,
  isDocumentHidden,
} from "src/utils";

/**
 * Get the progress/status of all the documents.
 * Maps from templateKey to DocProgress (which currently overwrites multiinstance documents)
 */
export const useDocProgressMap = (deviceId: string): DocProgressMap => {
  const { orgId = "" } = useParams();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId, templatesConfig);
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: user } = useGetUser();
  const { data: tasks } = useGetTasks(orgId, deviceId);

  return useMemo(() => {
    const newDocProgressMap: Record<string, DocProgress> = {};

    if (!documents || !device || !user || !templatesConfig) {
      return {};
    }

    // Process each document to build the progress map
    documents.forEach((doc) => {
      const docTemplate = doc.templateKey as string;
      const { incompleteTemplates } = getDocumentContexts({
        templateKey: docTemplate,
        documents,
        user,
        orgId,
        device,
        templatesConfig,
        tasks,
      });

      //TODO: change to doc.id and then we can fix the GeneratedDocViewer to only use this state
      newDocProgressMap[doc.id] = {
        docTemplate,
        docId: doc.id,
        functionalGroup:
          (doc.regulatoryFunctionalGroup as RegulatoryFunctionalGroup) ||
          RegulatoryFunctionalGroup.UNGROUPED,
        complete: isDocumentCompleted({
          document: doc,
          device,
          documents,
          documentVersion: doc.versions[0],
          templateConfig: templatesConfig[docTemplate],
        }),
        noVersion: doc.versions.length === 0,
        hidden: isDocumentHidden(
          docTemplate,
          device,
          templatesConfig,
          tasks,
          documents
        ),
        incompleteTemplates,
      };
    });

    return newDocProgressMap;
  }, [documents, device, user, orgId]);
};
